// Summary report panel (desktop parity):
//   Sales      → Sales Summary PDF        (question "salessummary-pdf", cash type)
//   Wholesales → Credit Sales Summary PDF (credit type)
//   Purchases  → Purchase Summary PDF     (question "purchasesummary-pdf")
//
// Runs on ppServer and streams a PDF; we download it via an object URL
// (same recipe as reports-page).

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

export type SummaryKind = 'sales-cash' | 'sales-credit' | 'purchase';

interface KeptSummaryView {
  from: string;
  to: string;
  notice: string;
  error: string;
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dayStartMs(iso: string): number {
  return new Date(`${iso}T00:00:00`).getTime();
}
function dayEndMs(iso: string): number {
  return new Date(`${iso}T23:59:59.999`).getTime();
}

@customElement('summary-report')
export class SummaryReport extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .page-scroll { flex: 1; overflow-y: auto; min-height: 0; padding: 1rem; }
    .page-title { margin: 0 0 0.25rem; font-size: 1.05rem; }
    .subtitle { margin: 0 0 1rem; font-size: 0.78rem; color: var(--color-text-secondary); }
    .filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.75rem; }
    .filters .fg { margin-bottom: 0; }
    .note { margin-top: 0.75rem; font-size: 0.8rem; color: var(--color-text-secondary); }
    .err { margin-top: 0.75rem; font-size: 0.8rem; color: var(--color-error, #d93025); }
    .ok { margin-top: 0.75rem; font-size: 0.8rem; color: var(--color-success, #188038); }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: String }) kind: SummaryKind = 'sales-cash';

  @state() private _from = todayIso();
  @state() private _to = todayIso();
  @state() private _busy = false;
  @state() private _error = '';
  @state() private _notice = '';

  private _view = new OutletViewCache<KeptSummaryView>(this, 'trade-summary', {
    capture: () => ({
      from: this._from,
      to: this._to,
      notice: this._notice,
      error: this._error,
    }),
    restore: (v) => {
      this._from = v.from;
      this._to = v.to;
      this._notice = v.notice;
      this._error = v.error;
      this._busy = false;
    },
    reset: () => {
      this._from = todayIso();
      this._to = todayIso();
      this._notice = '';
      this._error = '';
      this._busy = false;
    },
  });

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated() {
    this._view.switchTo(this.storeCode ? `${this.storeCode}|${this.kind}` : '');
  }

  private get _title(): string {
    switch (this.kind) {
      case 'sales-cash': return 'Sales Summary';
      case 'sales-credit': return 'Credit Sales Summary';
      default: return 'Purchase Summary';
    }
  }

  private async _generate() {
    if (!this.storeCode || this._busy) return;
    if (!this._from || !this._to) {
      this._error = 'Pick both dates';
      return;
    }
    this._busy = true;
    this._error = '';
    this._notice = '';
    const epoch = this._view.epoch;
    try {
      const from = dayStartMs(this._from);
      const to = dayEndMs(this._to);
      const blob =
        this.kind === 'purchase'
          ? await api.tradePurchaseSummaryPdf(this.storeCode, { from, to })
          : await api.tradeSalesSummaryPdf(this.storeCode, {
              type: this.kind === 'sales-credit' ? 'credit' : 'cash',
              from,
              to,
            });
      if (!this._view.isCurrent(epoch)) return;
      this._download(blob);
      this._notice = `${this._title} downloaded`;
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Summary failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._busy = false;
    }
  }

  private _download(blob: Blob) {
    const base = this._title.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'summary';
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp =
      `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
      `_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}_${stamp}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  render() {
    return html`
      <div class="page-scroll">
        <h2 class="page-title">${this._title}</h2>
        <p class="subtitle">
          ${this.kind === 'purchase'
            ? 'Purchase summary for the selected date range (PDF).'
            : `Summary of ${this.kind === 'sales-credit' ? 'wholesale (credit)' : 'cash'} sales for the selected date range (PDF).`}
        </p>
        <div class="filters">
          <div class="fg">
            <label>From</label>
            <input type="date" data-testid="summary-date-from" .value=${this._from} ?disabled=${this._busy}
              @change=${(e: Event) => { this._from = (e.target as HTMLInputElement).value; }}>
          </div>
          <div class="fg">
            <label>To</label>
            <input type="date" data-testid="summary-date-to" .value=${this._to} ?disabled=${this._busy}
              @change=${(e: Event) => { this._to = (e.target as HTMLInputElement).value; }}>
          </div>
          <button class="btn" ?disabled=${this._busy} data-testid="summary-generate" @click=${this._generate}>
            ${this._busy ? 'Generating…' : '⬇ Generate PDF'}
          </button>
        </div>
        ${this._busy ? html`<div class="note">The store is building the PDF — large ranges can take a while…</div>` : ''}
        ${this._error ? html`<div class="err">⚠ ${this._error}</div>` : ''}
        ${this._notice ? html`<div class="ok">✓ ${this._notice}</div>` : ''}
      </div>
    `;
  }
}
